import React, { useEffect, useCallback, useState } from "react";
import "../../App.css";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { UserSignUp } from "./Schema";
import { useFormik } from "formik";
import { lightAuthTheme, darkAuthTheme } from "../../Theme/authTheme";
import { ThemeProvider } from "@mui/material";
import { useDispatch } from "react-redux";
import { updateLoadingAxios } from "../../Redux/Reducers/uiSlice";
import { addUser, checkEmail } from "../../utils/restAPI";
import { debounce } from "lodash";
import { Alert, Button, Paper, Grid, IconButton } from "@mui/material";
import EmailVerification from "./EmailVerification";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { MuiTelInput } from 'mui-tel-input';
import { useNavigate } from "react-router-dom";
import AuthLogo from "./AuthLogo";
import { useTheme } from "../../contexts/ThemeContext";

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [showVerification, setShowVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailAvailable, setIsEmailAvailable] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailnotVerified, setEmailnotVerified] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [organizationName, setOrganizationName] = useState(
    "Individual Contractor"
  );

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  const initialValues = {
    name: "",
    lname: "",
    email: "",
    pass: "",
    confirmPass: "",
    phone: "",
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    handleSubmit,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: UserSignUp,
    onSubmit: async ({ name, lname, email, pass, phone }) => {
      dispatch(updateLoadingAxios(true));

      try {
        const response = await addUser({
          firstname: name,
          lastname: lname,
          email: email,
          phone: `+${phone.replace(/\D/g, "")}`,
          password: pass,
        });
        dispatch(updateLoadingAxios(false));

        if (response && response.success) {
          setEmailVerified(true);
          setEmailnotVerified(false);
          setEmail(email);
          setShowVerification(true);
        } else {
          setEmailnotVerified(true);
          setEmailVerified(false);
        }
      } catch (error) {
        setEmailVerified(false);
        setEmailnotVerified(true);
      }
    },
  });

  const handleSignInClick = () => {
    navigate("/login");
  };

  const checkOrganization = async (userEmail) => {
    try {
      const response = await checkEmail(userEmail);
      if (response && response.success) {
        setOrganizationName(response.data.organizationName);
      }
    } catch (error) {
      console.error("Error checking organization:", error);
    }
  };

  const debouncedCheckEmail = useCallback(
    debounce((email) => {
      if (email && email.length > 3) {
        checkOrganization(email);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (values.email) {
      debouncedCheckEmail(values.email);
    }
  }, [values.email, debouncedCheckEmail]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (showVerification) {
    return (
      <ThemeProvider theme={authTheme}>
        <EmailVerification email={email} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          zoom: 0.75,
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow: authTheme.palette.mode === 'dark' 
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <AuthLogo />
            </Box>

            {/* Welcome Text */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 1,
                }}
              >
                Create Account
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                }}
              >
                Join Vantage Alpha and start your investment journey today.
              </Typography>
            </Box>

            {/* Form */}
            <Box 
              component="form" 
              onSubmit={handleSubmit} 
              sx={{ mt: 3 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            >
              <Grid container spacing={2}>
                {/* First Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="name"
                    name="name"
                    label="First Name"
                    placeholder="Enter your first name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>

                {/* Last Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="lname"
                    name="lname"
                    label="Last Name"
                    placeholder="Enter your last name"
                    value={values.lname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lname && Boolean(errors.lname)}
                    helperText={touched.lname && errors.lname}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    placeholder="Enter your email address"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12}>
                  <MuiTelInput
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    placeholder="Enter your phone number"
                    value={values.phone}
                    onChange={(value) => setFieldValue("phone", value)}
                    onBlur={handleBlur}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    defaultCountry="US"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: authTheme.palette.mode === 'dark' ? "#374151" : "#F9FAFB",
                        border: `1px solid ${authTheme.palette.mode === 'dark' ? "#4B5563" : "#E5E7EB"}`,
                        "&:hover": {
                          backgroundColor: authTheme.palette.mode === 'dark' ? "#4B5563" : "#FFFFFF",
                          borderColor: authTheme.palette.mode === 'dark' ? "#6B7280" : "#D1D5DB",
                        },
                        "&.Mui-focused": {
                          backgroundColor: authTheme.palette.mode === 'dark' ? "#4B5563" : "#FFFFFF",
                          borderColor: authTheme.palette.mode === 'dark' ? "#818CF8" : "#6366F1",
                          borderWidth: "2px",
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Password */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="pass"
                    name="pass"
                    label="Password"
                    placeholder="Create a strong password"
                    type={showPassword ? "text" : "password"}
                    value={values.pass}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.pass && Boolean(errors.pass)}
                    helperText={touched.pass && errors.pass}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: authTheme.palette.text.secondary }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="confirmPass"
                    name="confirmPass"
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={values.confirmPass}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.confirmPass && Boolean(errors.confirmPass)}
                    helperText={touched.confirmPass && errors.confirmPass}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                          sx={{ color: authTheme.palette.text.secondary }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>

                {/* Error Messages */}
                {emailnotVerified && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      Something went wrong. Please try again.
                    </Alert>
                  </Grid>
                )}

                {/* Sign Up Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      mt: 2,
                      height: "56px",
                      fontSize: "1rem",
                      fontWeight: 600,
                      backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                      color: authTheme.palette.mode === 'dark' ? "#ffffff" : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                      },
                    }}
                  >
                    Create Account
                  </Button>
                </Grid>

                {/* Sign In Link */}
                <Grid item xs={12}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: authTheme.palette.text.secondary }}>
                      Already have an account?{" "}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleSignInClick}
                        sx={{
                          color: authTheme.palette.secondary.main,
                          textDecoration: "none",
                          fontWeight: 600,
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Sign In
                      </Link>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default SignUp;
